import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ChaveValorService } from 'shared/services/chaveValor/chaveValorService';
import { IListagemChaveValor, IResponseChaveValor } from 'shared/types/chaveValor';

import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { useHasPermissionAcao } from 'shared/hooks';
import { useSelectedOrganization } from 'shared/store/organization';
import { stringToDateTime } from 'shared/utils';

export interface IKeyValue {
  key: string,
  value: string
}

export const ListagemDeChaveValor: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const org = useSelectedOrganization();

  const navigate = useNavigate();Number(org?.CodigoOrganizacao);
  const { t } = useTranslation();

  const hasPermitionCreate = useHasPermissionAcao('configChaveValor', 'CRE');
  const hasPermitionRead = useHasPermissionAcao('configChaveValor', 'REA');


  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);

  const [rows, setRows] = useState<IListagemChaveValor[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({ ...stateSnack, open: false });
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString());
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  useEffect(() => {
    
    ChaveValorService.getAll(searchParams.toString())
      .then((result) => {
        setIsLoading(false);

        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          
          setTotalCount(result.count);
          setRows(result.rows);
          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString()) / Number.parseInt(limit.toString()));
        }
      });
  }, [eventoPesquisar]);

  const listKeyValueStatus: IKeyValue[] = [
    {
      key: '10',
      value: 'Campanhas'
    }, 
    // {
    //   key: '3',
    //   value: 'Motor Financeiro'
    // }, 
  ];
  

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handlePesquisar = () => {
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const buscaCodigo = useMemo(() => {
    return searchParams.get('Codigo') || [];
  }, [searchParams]);

  const buscaDesc = useMemo(() => {
    return searchParams.get('desc') || '';
  }, [searchParams]);

  // const buscaSistema = useMemo(() => {
  //   return searchParams.get('Sistema') || '';
  // }, [searchParams]);

  
  const listaFiltros: IListaFiltros[] = [
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.chaveValor.codigo'),
      textoBusca: buscaCodigo,
      handleTextoBusca: texto => { searchParams.set('Codigo', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: t('pages.chaveValor.desc'),
      textoBusca: buscaDesc,
      handleTextoBusca: texto => { searchParams.set('desc', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    // {
    //   tipo: 'Select',
    //   mapValue: listKeyValueStatus.map((itemk) => itemk),
    //   label: t('pages.chaveValor.sistema'),
    //   textoBusca: buscaSistema,
    //   handleTextoBusca: texto => { searchParams.set('Sistema', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    // },
    
  ];

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.chaveValor.titulo')}
        botaoLog={
          <Button
            color='info'
            disableElevation
            variant='contained'
            size='small'
            onClick={() => navigate('/configChaveValor/listagem/logs')}
            startIcon={<Icon>crop</Icon>}
          >
            <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
              {t('components.ferramentasDaListagem.logs')}
            </Typography>
          </Button>
        }

        drawerDeFiltros={
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => handlePesquisar()}
            handleLimpar={() => handleLimpar()}
          />
        }
      >     

        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell>{t('global.acoes')}</TableCell>               
                <TableCell>{t('pages.chaveValor.sistema')}</TableCell>
                <TableCell>{t('pages.chaveValor.codigo')}</TableCell>
                <TableCell>{t('pages.chaveValor.desc')}</TableCell>
                <TableCell sx={{ maxWidth: 10 }}>{t('pages.chaveValor.valor')}</TableCell>
                <TableCell>{t('pages.chaveValor.alt')}</TableCell>
                <TableCell>{t('pages.chaveValor.dhalt')}</TableCell>
              </TableRow>
            </TableHead>
            { !isLoading && (<TableBody>
              {rows.map((row) => (
                <TableRow key={row.Codigo}>
                  { <TableCell>
                    <IconButton size="small" disabled={!hasPermitionRead} onClick={() => navigate(`/configChaveValor/detalhe/${row.Codigo}`)}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </TableCell> }
                  <TableCell>{row.NomeSistema}</TableCell>
                  <TableCell>{row.Codigo}</TableCell>
                  <TableCell>{row.Descricao}</TableCell>   
                  <TableCell  width={100}>{row.Mascara == 'N' ? row.Valor: '*******'}</TableCell> 
                  <TableCell>{row.usuAlteracao}</TableCell>
                  <TableCell>{stringToDateTime(row.dhAlteracao)}</TableCell>
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={2}>
                   


                  </TableCell>
                  <TableCell colSpan={8}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[5, 8, 10, 20, 50, 100]}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
      </LayoutBaseDePagina>
      <AlertaSnackBar
        open={stateSnack.open}
        severity={stateSnack.severity as AlertColor}
        message={stateSnack.message}
        handleClose={handleCloseSnack}
      />
    </>
  );
};